import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Rocket } from "lucide-react";
import { formatApiError } from "@/auth/AuthContext";

const when = (value) => value ? new Date(value).toLocaleString("en-NZ", { timeZone: "Pacific/Auckland" }) : "—";

export default function PlatformDeploy({ api }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [output, setOutput] = useState("");
  const [error, setError] = useState("");
  const [connection, setConnection] = useState("");
  const [starting, setStarting] = useState(false);
  const offset = useRef(0);
  const logView = useRef(null);
  const selectedRef = useRef(null);
  const follow = useRef(true);

  const select = (id) => {
    if (selectedRef.current === id) return;
    selectedRef.current = id;
    offset.current = 0;
    follow.current = true;
    setOutput("");
    setSelected(id);
  };

  useEffect(() => {
    let stopped = false;
    let timer;
    const poll = async () => {
      try {
        const response = await axios.get(`${api}/platform/deployments`, { silent: true, timeout: 10000 });
        if (stopped) return;
        setData(response.data);
        const chosen = response.data.items.find((item) => item.id === selectedRef.current);
        if (chosen && chosen.status !== "queued") {
          setError((message) => message.startsWith("Deployment queued,") ? "" : message);
        }
        if (!selectedRef.current && response.data.items.length) select(response.data.items[0].id);
        const id = selectedRef.current;
        if (id) {
          const log = await axios.get(`${api}/platform/deployments/${id}/log`, { params: { offset: offset.current }, silent: true, timeout: 10000 });
          if (stopped || selectedRef.current !== id) return;
          offset.current = log.data.offset;
          setOutput((text) => text + log.data.text);
        }
        setConnection("");
      } catch (ex) {
        if (!stopped) setConnection(ex.response?.status === 403 || ex.response?.status === 401
          ? "Your session no longer has deployment access. Sign in again."
          : "Waiting for the server to reconnect. Logs will resume automatically when it returns.");
      } finally {
        if (!stopped) timer = setTimeout(poll, 1500);
      }
    };
    poll();
    return () => { stopped = true; clearTimeout(timer); };
  }, [api]);

  useEffect(() => {
    if (follow.current && logView.current) logView.current.scrollTop = logView.current.scrollHeight;
  }, [output]);

  const deploy = async () => {
    setStarting(true);
    setError("");
    try {
      const response = await axios.post(`${api}/platform/deployments`);
      select(response.data.id);
      setData((current) => ({ ...current, active: true, items: [response.data, ...current.items] }));
    } catch (ex) {
      if (typeof ex.response?.data?.detail === "string" && ex.response.data.detail.startsWith("Deployment queued,")) select(null);
      setError(ex.response?.data?.detail ? formatApiError(ex.response.data.detail)
        : "The server disconnected before confirming the request. Wait for history to refresh before trying again.");
    } finally {
      setStarting(false);
    }
  };

  const current = data?.items.find((item) => item.id === selected);
  const card = "rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] p-5";
  return <div className="space-y-6" data-testid="platform-deploy">
    <section className={card}>
      <h3 className="font-head text-lg font-semibold text-[var(--hp-heading)]">Deploy the platform</h3>
      <p className="mt-2 text-sm text-[var(--hp-muted)]">Run the server’s update.cmd script. The platform may briefly be unavailable while services restart; this screen will reconnect automatically.</p>
      {data?.trigger_mode === "scheduled" && <p className="mt-2 text-sm text-[var(--hp-muted)]">Deployment starts when the scheduled task next checks the queue, normally within one minute.</p>}
      {data && !data.enabled && <p className="mt-3 text-sm text-[var(--hp-gold)]">{data.reason}</p>}
      <button data-testid="platform-deploy-button" onClick={deploy} disabled={!data?.enabled || data.active || starting || !!connection}
        className="mt-4 inline-flex items-center gap-2 rounded-full bg-[var(--hp-primary)] px-5 py-2.5 text-sm font-bold text-[#04231a] hover:bg-[var(--hp-primary-hover)] disabled:opacity-40">
        <Rocket className="h-4 w-4" />{starting ? "Starting…" : data?.active ? "Deployment in progress…" : "Deploy"}
      </button>
      {error && <p role="alert" className="mt-3 text-sm text-[var(--hp-loss)]">{error}</p>}
      {connection && <p role="status" className="mt-3 text-sm text-[var(--hp-gold)]">{connection}</p>}
    </section>
    <section className={card}>
      <div className="flex flex-wrap justify-between gap-2"><h3 className="font-head text-lg font-semibold text-[var(--hp-heading)]">Deployment log</h3>
        {current && <span className="text-sm text-[var(--hp-muted)]">{when(current.requested_at)} · {current.status}</span>}</div>
      <pre ref={logView} data-testid="platform-deploy-log" tabIndex={0} aria-label="Deployment output"
        onScroll={() => { const el = logView.current; follow.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40; }}
        className="mt-4 h-80 overflow-auto rounded-xl border border-[var(--hp-border)] bg-[var(--hp-bg)] p-4 text-xs leading-relaxed text-[var(--hp-text)] whitespace-pre-wrap break-words">{output || (selected ? "Waiting for script output…" : "Deployment output will appear here.")}</pre>
      {current?.error && <p className="mt-2 text-sm text-[var(--hp-loss)]">{current.error}</p>}
    </section>
    <section className={card}>
      <h3 className="font-head text-lg font-semibold text-[var(--hp-heading)]">Deployment history</h3>
      <p className="mt-1 text-xs text-[var(--hp-muted)]">Latest 100 deployments. Times are shown in New Zealand time.</p>
      {!data ? <p className="mt-4 text-sm text-[var(--hp-muted)]">Loading…</p> : !data.items.length ? <p className="mt-4 text-sm text-[var(--hp-muted)]">No deployments recorded yet.</p>
        : <ul className="mt-3 divide-y divide-[var(--hp-border)]">{data.items.map((item) => <li key={item.id} className="py-4" data-testid="platform-deploy-history-row">
          <div className="flex flex-wrap items-center gap-3"><button onClick={() => select(item.id)} className="text-sm font-semibold text-[var(--hp-heading)] hover:underline">{when(item.started_at || item.requested_at)}</button>
            <span className={`text-xs font-semibold ${item.status === "failed" ? "text-[var(--hp-loss)]" : item.status === "succeeded" ? "text-[var(--hp-profit)]" : "text-[var(--hp-gold)]"}`}>{item.status}</span>
            <span className="text-xs text-[var(--hp-muted)]">{item.requested_by}{item.finished_at ? ` · finished ${when(item.finished_at)}` : ""}</span></div>
          <p className="mt-2 whitespace-pre-wrap break-words text-sm text-[var(--hp-text)]">{item.commit?.message || (item.status === "queued" || item.status === "running" ? "Commit details will be recorded when the script finishes." : "Commit details unavailable.")}</p>
          {item.commit?.sha && <p className="mt-1 font-mono text-xs text-[var(--hp-muted)]">{item.commit.sha.slice(0, 12)}</p>}
          {item.error && <p className="mt-2 text-xs text-[var(--hp-loss)]">{item.error}</p>}
        </li>)}</ul>}
    </section>
  </div>;
}
